import { useMemo } from 'react';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type { PageTreeNode } from './pageTreeTypes.ts';
import {
	type PtPageRawNode,
	type SortField,
	mapPtPageNode,
	sortNodes,
	extractChildNodes,
	parseRootType,
	parsePageTitle,
	buildTitleSearchCql,
	resolveRootId,
	needsAncestorExpansion,
	needsRootChildren,
	CHILDREN_PAGE_SIZE,
} from './pageTreeUtils.ts';
import PageTreeMacroChildrenQuery from './queries/__generated__/PageTreeMacroChildrenQuery.graphql.ts';
import type {
	PageTreeMacroChildrenQuery$data,
	PageTreeMacroChildrenQuery$variables,
} from './queries/__generated__/PageTreeMacroChildrenQuery.graphql.ts';
import PageTreeMacroAncestorsQuery from './queries/__generated__/PageTreeMacroAncestorsQuery.graphql.ts';
import type {
	PageTreeMacroAncestorsQuery$data,
	PageTreeMacroAncestorsQuery$variables,
} from './queries/__generated__/PageTreeMacroAncestorsQuery.graphql.ts';
import PageTreeMacroSpaceHomepageQuery from './queries/__generated__/PageTreeMacroSpaceHomepageQuery.graphql.ts';
import type {
	PageTreeMacroSpaceHomepageQuery$data,
	PageTreeMacroSpaceHomepageQuery$variables,
} from './queries/__generated__/PageTreeMacroSpaceHomepageQuery.graphql.ts';
import PageTreeMacroRootLevelQuery from './queries/__generated__/PageTreeMacroRootLevelQuery.graphql.ts';
import type {
	PageTreeMacroRootLevelQuery$data,
	PageTreeMacroRootLevelQuery$variables,
} from './queries/__generated__/PageTreeMacroRootLevelQuery.graphql.ts';
import PageTreeMacroSearchByTitleQuery from './queries/__generated__/PageTreeMacroSearchByTitleQuery.graphql.ts';
import type {
	PageTreeMacroSearchByTitleQuery$data,
	PageTreeMacroSearchByTitleQuery$variables,
} from './queries/__generated__/PageTreeMacroSearchByTitleQuery.graphql.ts';

interface UsePageTreeDataOptions {
	contentId: string;
	rootParam: string | null;
	spaceKey: string;
	sort: SortField;
	reverse: boolean;
}

export interface UsePageTreeDataResult {
	nodes: PageTreeNode[] | null;
	ancestorIds: string[];
	loading: boolean;
	error: string | null;
	/**
	 * Whether the configured root could be resolved to a page.
	 *
	 * This is distinct from whether `nodes` is non-empty: a root can resolve
	 * successfully yet have zero children (a valid, empty tree). Only when the
	 * root itself cannot be resolved (e.g. a title/@home/@parent root that
	 * points at nothing) is this `false`. Mirrors the legacy backend, which
	 * only reports "root page not found" when the root page object is null
	 * (PageTree.java) and otherwise renders an empty tree.
	 *
	 * `@none` lists top-level pages with no specific root, so it is always
	 * considered resolved. `@self` resolves because its rootId is the current
	 * page (contentId).
	 */
	rootResolved: boolean;
	/**
	 * The resolved root page id. Used both to fetch the root's children and as
	 * the "ancestor" scope when submitting a page-tree search:
	 * - `@self`   → current page (contentId)
	 * - `@home`   → space homepage id
	 * - `@parent` → parent page id
	 * - title     → matched page id
	 * - `@none`   → `undefined` (search is performed without an ancestor)
	 */
	rootId: string | undefined;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const extractRootLevelNodes = (
	data: PageTreeMacroRootLevelQuery$data | undefined,
	sort: string,
	reverse: boolean,
): PageTreeNode[] | null => {
	const firstPage = data?.ptpage;
	if (!firstPage) {
		return null;
	}

	const rawPages: PtPageRawNode[] = [firstPage as PtPageRawNode];
	const siblings = firstPage.followingSiblings?.nodes;
	if (siblings) {
		for (const s of siblings) {
			if (s) {
				rawPages.push(s as PtPageRawNode);
			}
		}
	}
	return sortNodes(rawPages.map(mapPtPageNode), sort, reverse);
};

const extractAncestorIds = (data: PageTreeMacroAncestorsQuery$data | undefined): string[] => {
	const ancestors = data?.ptpage?.nearestAncestors?.nodes;
	if (!ancestors) {
		return [];
	}
	const ids: string[] = [];
	for (const a of ancestors) {
		if (a?.id) {
			ids.push(a.id);
		}
	}
	return ids;
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export const usePageTreeData = ({
	contentId,
	rootParam,
	spaceKey,
	sort,
	reverse,
}: UsePageTreeDataOptions): UsePageTreeDataResult => {
	const rootType = parseRootType(rootParam);
	const pageTitle = rootType === 'title' && rootParam ? parsePageTitle(rootParam) : undefined;

	// Build CQL for title search (stable across renders)
	const cql = useMemo(
		() => (rootType === 'title' && pageTitle ? buildTitleSearchCql(pageTitle, spaceKey) : ''),
		[rootType, pageTitle, spaceKey],
	);

	// --- Step 1: Root resolution queries (only one runs based on rootType) ---

	const homepageResult = useQueryInterop<
		PageTreeMacroSpaceHomepageQuery$data,
		PageTreeMacroSpaceHomepageQuery$variables
	>(PageTreeMacroSpaceHomepageQuery, {
		variables: { spaceKey },
		fetchPolicy: 'cache-first',
		skip: rootType !== 'home',
	});

	const parentAncestorResult = useQueryInterop<
		PageTreeMacroAncestorsQuery$data,
		PageTreeMacroAncestorsQuery$variables
	>(PageTreeMacroAncestorsQuery, {
		variables: { contentId },
		fetchPolicy: 'cache-first',
		skip: rootType !== 'parent',
	});

	const searchResult = useQueryInterop<
		PageTreeMacroSearchByTitleQuery$data,
		PageTreeMacroSearchByTitleQuery$variables
	>(PageTreeMacroSearchByTitleQuery, {
		variables: { cql },
		fetchPolicy: 'cache-first',
		skip: rootType !== 'title' || !pageTitle,
	});

	const rootLevelResult = useQueryInterop<
		PageTreeMacroRootLevelQuery$data,
		PageTreeMacroRootLevelQuery$variables
	>(PageTreeMacroRootLevelQuery, {
		variables: { spaceKey },
		fetchPolicy: 'cache-first',
		skip: rootType !== 'none',
	});

	// Derive rootId from whichever query ran
	const rootId = resolveRootId({
		rootType,
		contentId,
		homepageData: homepageResult.data,
		ancestorData: parentAncestorResult.data,
		searchData: searchResult.data,
	});

	// `@none` lists top-level pages with no specific root, so it can never be
	// "not found". For every other root type — including `@self`, whose rootId
	// is the current page (contentId) — resolution succeeds only when a rootId
	// was derived. Mirrors the legacy backend's `!noRoot && rootPage == null`.
	const rootResolved = rootType === 'none' || rootId != null;

	// --- Step 2: Fetch root's children (depends on rootId) ---

	const childrenResult = useQueryInterop<
		PageTreeMacroChildrenQuery$data,
		PageTreeMacroChildrenQuery$variables
	>(PageTreeMacroChildrenQuery, {
		variables: { contentId: rootId ?? '', first: CHILDREN_PAGE_SIZE, after: null },
		fetchPolicy: 'cache-first',
		skip: !rootId || !needsRootChildren(rootType),
	});

	// --- Step 3: Fetch ancestors for auto-expansion ---

	const ancestorsResult = useQueryInterop<
		PageTreeMacroAncestorsQuery$data,
		PageTreeMacroAncestorsQuery$variables
	>(PageTreeMacroAncestorsQuery, {
		variables: { contentId },
		fetchPolicy: 'cache-first',
		skip: !needsAncestorExpansion(rootType),
	});

	// --- Derive final state ---

	// Skipped queries return loading: false, so only active queries contribute
	const loading =
		homepageResult.loading ||
		parentAncestorResult.loading ||
		searchResult.loading ||
		rootLevelResult.loading ||
		childrenResult.loading;

	const error =
		homepageResult.error?.message ??
		parentAncestorResult.error?.message ??
		searchResult.error?.message ??
		rootLevelResult.error?.message ??
		childrenResult.error?.message ??
		ancestorsResult.error?.message ??
		null;

	const nodes = useMemo(() => {
		if (loading) {
			return null;
		}
		if (rootType === 'none') {
			return extractRootLevelNodes(rootLevelResult.data, sort, reverse);
		}
		if (!rootId) {
			return null;
		}
		return extractChildNodes(childrenResult.data, sort, reverse);
	}, [loading, rootType, rootId, rootLevelResult.data, childrenResult.data, sort, reverse]);

	const ancestorIds = useMemo(() => {
		if (!needsAncestorExpansion(rootType)) {
			return [];
		}
		const allAncestors = extractAncestorIds(ancestorsResult.data);
		if (rootType === 'none') {
			return allAncestors;
		}
		if (!rootId) {
			return [];
		}
		const rootIndex = allAncestors.indexOf(rootId);
		if (rootIndex >= 0) {
			return allAncestors.slice(0, rootIndex + 1);
		}
		return [];
	}, [rootType, rootId, ancestorsResult.data]);

	return { nodes, ancestorIds, loading, error, rootResolved, rootId };
};
